import CELL_ATTRIBUTES, {
  MISC_KEYS,
  TCellAttributesAreasKeys,
} from "./CellAttributesDefinition";
import type {
  IFeaturesAllowed,
  ISlotData,
  TContainerLengths,
} from "open-vessel-definition";

import { RecursiveKeyOf } from "@tedivo/tedivo-pure-helpers";
import { TReducerISlotDataActions } from "@tedivo/tedivo-bay-grid-pure";
import { reeferIconPath } from "./createSlotCapabilitiesSvg";

export function getSlotCapabilitiesLegendOptions(
  availableSizes: TContainerLengths[] | undefined,
  areas: TCellAttributesAreasKeys[],
  featuresAllowed?: IFeaturesAllowed,
  isDarkMode?: boolean,
): ILegendOptions[] {
  const allContainerLenghts = Object.keys(CELL_ATTRIBUTES.sizes).map(
    Number,
  ) as TContainerLengths[];

  const options: ILegendOptions[] = [];

  const areasFnsMap: Record<TCellAttributesAreasKeys, () => void> = {
    sizes: addSizesOptions,
    misc: addMiscOptions,
    restricted: addRestrictedOptions,
    sizesWithCones: addConesOptions,
  };

  function addSizesOptions() {
    allContainerLenghts
      .filter(
        (size) =>
          !availableSizes ||
          availableSizes.length === 0 ||
          availableSizes.indexOf(size) >= 0,
      )
      .forEach((size) => {
        const attrs = CELL_ATTRIBUTES.sizes[size];
        options.push({
          id: attrs.reducerAddKey,
          bgColor: isDarkMode
            ? attrs.darkBgColor || attrs.bgColor
            : attrs.bgColor,
          color: isDarkMode ? attrs.darkColor || attrs.color : attrs.color,
          labelKey: `slotCapabilities.size.${attrs.text || size}`,
          keyName: attrs.keyName,
        });
      });
  }

  function addMiscOptions() {
    MISC_KEYS.forEach((k) => {
      const addOption =
        (k === "coolStowProhibited" &&
          featuresAllowed?.slotCoolStowProhibited) ||
        (k === "hazardousProhibited" &&
          featuresAllowed?.slotHazardousProhibited) ||
        k === "reefer";

      if (addOption) {
        const attrs = CELL_ATTRIBUTES.misc[k];

        const iconSvg =
          k === "reefer"
            ? (st: string, cn: string) =>
                `<svg viewBox="0 0 170 170" style="${
                  st || ""
                }" class="${cn}"><path d="${reeferIconPath}"/></svg>`
            : undefined;

        options.push({
          id: attrs.reducerAddKey,
          bgColor: isDarkMode
            ? attrs.darkBgColor || attrs.bgColor
            : attrs.bgColor,
          color: isDarkMode ? attrs.darkColor || attrs.color : attrs.color,
          labelKey: `slotCapabilities.misc.${attrs.text || k}`,
          keyName: attrs.keyName,
          iconSvg,
        });
      }
    });
  }

  function addRestrictedOptions() {
    const attrs = CELL_ATTRIBUTES.restricted.restricted;

    options.push({
      id: attrs.reducerAddKey,
      bgColor: isDarkMode ? attrs.darkBgColor || attrs.bgColor : attrs.bgColor,
      color: isDarkMode ? attrs.darkColor || attrs.color : attrs.color,
      labelKey: `slotCapabilities.restricted.${attrs.text}`,
      keyName: attrs.keyName,
    });
  }

  function addConesOptions() {
    allContainerLenghts
      .filter(
        (size) =>
          !availableSizes ||
          availableSizes.length === 0 ||
          availableSizes.indexOf(size) >= 0,
      )
      .forEach((size) => {
        const attrs = CELL_ATTRIBUTES.sizesWithCones[size];
        options.push({
          id: attrs.reducerAddKey,
          bgColor: isDarkMode
            ? attrs.darkBgColor || attrs.bgColor
            : attrs.bgColor,
          color: isDarkMode ? attrs.darkColor || attrs.color : attrs.color,
          labelKey: `slotCapabilities.size.${attrs.text || size}`,
          keyName: attrs.keyName,
        });
      });
  }

  areas.forEach((areaName) => areasFnsMap[areaName]());

  return options;
}

export interface ILegendOptions {
  id: TReducerISlotDataActions;
  color: string;
  bgColor: string;
  labelKey: string;
  keyName: RecursiveKeyOf<ISlotData>;
  iconSvg?: (style: string, className: string) => string;
}

export interface ISlotShipRestrictions {
  allowCoolStowProhibited?: boolean;
  allowHazardousProhibited?: boolean;
}
